import { ComponentProps, ReactNode, useContext, forwardRef, Ref } from "react"

// Router
import { NavLink as RouterNavLink, useMatch } from "@/lib/router"

// UI
import { cn } from "@/lib/utils"

// Context
import { AuthLayoutContext } from "@/layouts/AuthLayout"

// Analytics
import { sendEvent } from "@/lib/analytics"

// Utils
import { normaliseString } from "@/lib/js"

export const NavLink = forwardRef<
	HTMLDivElement | HTMLAnchorElement,
	{
		href?: string
		icon: (props: ComponentProps<"svg">) => ReactNode
		name: string
		children?: ReactNode
		open?: boolean
	}
>(({ name, icon: Icon, href = "" }, ref) => {
	const { setMobileMenuOpen } = useContext(AuthLayoutContext)

	const current = useMatch(href === "/" ? { path: href, end: true } : href)

	const NavContent = () => {
		return (
			<>
				<Icon
					className="mr-2 size-6 shrink-0 text-gray-400 group-hover:text-gray-500"
					aria-hidden="true"
				/>
				{name}
			</>
		)
	}

	return href ? (
		<RouterNavLink
			ref={ref as Ref<HTMLAnchorElement>}
			to={href}
			className={cn(
				current
					? "bg-gray-100 text-gray-900"
					: "text-gray-600 hover:bg-gray-50 hover:text-gray-900",
				"group flex items-center rounded-md px-2 py-2 text-sm font-medium",
			)}
			onClick={() => {
				setMobileMenuOpen(false)
				sendEvent("layout", "navitem_onclick", {
					label: normaliseString(name),
				})
			}}
		>
			<NavContent />
		</RouterNavLink>
	) : (
		<div
			className={cn(
				current
					? "pointer-events-none bg-gray-100 text-gray-900"
					: "pointer-events-none text-gray-600 hover:bg-gray-50 hover:text-gray-900",
				"group flex items-center rounded-md px-2 py-2 text-sm font-medium",
			)}
		>
			<NavContent />
		</div>
	)
})
NavLink.displayName = "NavLink"
